// Per-status tooltip helper for deliverable status pills.
// Returns the hover text contract managers see on the status badge,
// or undefined when the relevant timestamp is missing so callers can
// skip the `title` attribute entirely (preventing "Submitted
// undefined · ..." half-formed copy).

import type { Deliverable } from "../../../../types";

function formatDateGB(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function deliverableStatusTooltip(
  d: Deliverable,
): string | undefined {
  switch (d.status) {
    case "not-started": {
      const date = formatDateGB(d.dueDate);
      if (!date) return undefined;
      return `Awaiting first submission · due ${date}`;
    }
    case "submitted": {
      const date = formatDateGB(d.submittedAt);
      if (!date) return undefined;
      return `Submitted ${date} · awaiting customer review`;
    }
    case "under-review": {
      const date = formatDateGB(d.submittedAt);
      if (!date) return undefined;
      return `Submitted ${date} · under review`;
    }
    case "revision-requested": {
      // Fall back to updatedAt — when a deliverable is bounced back
      // for revision the updatedAt timestamp is bumped by the
      // transition itself, so it's the closest signal of "when this
      // bounce happened" until a dedicated revisionRequestedAt
      // column exists.
      const date = formatDateGB(d.updatedAt);
      if (!date) return undefined;
      return `Bounced ${date} · awaiting revised submission`;
    }
    case "accepted": {
      const date = formatDateGB(d.acceptedAt);
      if (!date) return undefined;
      return `Accepted ${date}`;
    }
    case "rejected": {
      const date = formatDateGB(d.rejectedAt);
      if (!date) return undefined;
      const reason = d.rejectionReason?.trim();
      return reason
        ? `Rejected ${date} · ${reason}`
        : `Rejected ${date}`;
    }
    case "closed": {
      const date = formatDateGB(d.updatedAt);
      if (!date) return undefined;
      return `Closed ${date}`;
    }
    default:
      return undefined;
  }
}
